import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { EmailDeliveryPolicy, WebhookDeliveryPolicy } from "@fmc/contracts";
import { PageSection, ReportTable, Pagination, StatusBadge } from "../components/reporting";
import type { Column } from "../components/reporting";
import { useScreenInit } from "../useScreenInit";
import { useSession } from "../session";
import { PayloadPreview } from "../components/PayloadPreview";
import { typeMeta } from "../data/destinations";
import type { Destination, DestinationType } from "../data/destinations";
import { api } from "../api";

/* ------------------------------------------------------------------ */
/* Shared bits                                                         */
/* ------------------------------------------------------------------ */

const inputCls =
  "w-full h-9 px-3 rounded-xs border border-default bg-surface-white text-primary placeholder:text-tertiary font-mono text-caption focus:outline-none focus:border-brand-red/50 transition-colors";

const textCls =
  "w-full h-9 px-3 rounded-xs border border-default bg-surface-white text-body text-primary placeholder:text-tertiary focus:outline-none focus:border-brand-red/50 transition-colors";

const webhookDeliveryPolicies: Array<{
  id: WebhookDeliveryPolicy;
  label: string;
  description: string;
}> = [
  {
    id: "every_exponential",
    label: "Every accepted lead - exponential retry",
    description: "Send immediately and retry failures with exponential backoff.",
  },
  {
    id: "every_single",
    label: "Every accepted lead - single retry",
    description: "Send immediately and retry once if delivery fails.",
  },
  {
    id: "daily_exponential",
    label: "Daily leads - exponential retry",
    description: "Send one daily batch at 08:00 London time with exponential retries.",
  },
  {
    id: "daily_single",
    label: "Daily leads - single retry",
    description: "Send one daily batch at 08:00 London time and retry once on failure.",
  },
];

const emailDeliveryPolicies: Array<{
  id: EmailDeliveryPolicy;
  label: string;
  description: string;
}> = [
  {
    id: "immediate",
    label: "Every lead - immediate",
    description: "Send a separate email as soon as each qualified lead is accepted.",
  },
  {
    id: "daily",
    label: "Batched leads - daily",
    description: "Send all leads received since the previous email together at 08:00 each day.",
  },
];

function webhookPolicyLabel(policy: WebhookDeliveryPolicy | undefined): string {
  return (
    webhookDeliveryPolicies.find((option) => option.id === policy)?.label ??
    webhookDeliveryPolicies[0]!.label
  );
}

function emailPolicyLabel(policy: EmailDeliveryPolicy | undefined): string {
  return (
    emailDeliveryPolicies.find((option) => option.id === policy)?.label ??
    emailDeliveryPolicies[0]!.label
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <label className="text-caption font-medium text-primary">{label}</label>
        {hint && <span className="text-micro text-tertiary">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function Toggle({ on, onToggle, label }: { on: boolean; onToggle: () => void; label: string }) {
  return (
    <button onClick={onToggle} className="flex items-center justify-between w-full py-1 group">
      <span className="text-body text-secondary group-hover:text-primary transition-colors">
        {label}
      </span>
      <span
        className={`relative w-10 h-6 rounded-full transition-colors shrink-0 ${
          on ? "bg-brand-red" : "bg-surface-light border border-default"
        }`}
      >
        <span
          className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-surface-white shadow-[var(--shadow-sm)] transition-all ${
            on ? "left-[19px]" : "left-[3px]"
          }`}
        />
      </span>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* HubSpot intake summary                                              */
/* ------------------------------------------------------------------ */

function HubspotIntakeSummary({ brand }: { brand: string }) {
  const [metrics, setMetrics] = useState<{
    received30d: number;
    lastReceivedAt: string | null;
  } | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api<{ received30d: number; lastReceivedAt: string | null }>("/api/delivery/inbound")
      .then(setMetrics)
      .catch((problem) =>
        setError(problem instanceof Error ? problem.message : "Inbound status unavailable"),
      );
  }, []);
  const lastReceived = metrics?.lastReceivedAt
    ? new Date(metrics.lastReceivedAt).toLocaleString("en-GB")
    : "No events yet";

  return (
    <div className="bg-surface-white border border-default rounded-xl px-[26px] py-[22px]">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-md bg-surface-light border border-default flex items-center justify-center">
            <i className="fa-brands fa-hubspot text-[20px]" style={{ color: "#FF7A59" }} />
          </span>
          <div>
            <h3 className="text-heading font-semibold text-primary">FMC HubSpot → {brand}</h3>
            <p className="text-caption text-tertiary mt-0.5">
              Leads captured in FMC's HubSpot are posted to your endpoint the moment they qualify.
            </p>
            {error && <p className="text-caption text-status-red mt-1">{error}</p>}
          </div>
        </div>
        <div className="flex items-center gap-5">
          <div className="text-right">
            <div className="text-title-md font-bold text-primary leading-none">
              {metrics?.received30d ?? "—"}
            </div>
            <div className="text-micro text-tertiary mt-1">Received (30d)</div>
          </div>
          <span className="w-px h-8 bg-default" />
          <div className="text-right">
            <div className="text-body font-semibold text-primary leading-none">{lastReceived}</div>
            <div className="text-micro text-tertiary mt-1">Last accepted</div>
          </div>
          <StatusBadge label="Receiving" tone="green" />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Destination card                                                    */
/* ------------------------------------------------------------------ */

function DestinationCard({
  dest,
  canEdit,
  onRefresh,
}: {
  dest: Destination;
  canEdit: boolean;
  onRefresh: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(dest.active);
  const [tested, setTested] = useState(false);
  const [verificationResent, setVerificationResent] = useState(false);
  const [error, setError] = useState("");
  const meta = typeMeta[dest.type];
  const isEmail = dest.type === "email";
  const isDailyWebhook = Boolean(!isEmail && dest.deliveryPolicy?.startsWith("daily"));
  const isDailyEmail = isEmail && dest.deliveryPolicy === "daily";

  return (
    <div className="bg-surface-white border border-default rounded-xl p-[22px]">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-10 h-10 rounded-md bg-surface-light border border-default flex items-center justify-center shrink-0">
            <i className={`fa-solid ${meta.icon} text-[15px] text-secondary`} />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-heading font-semibold text-primary truncate">{dest.name}</h3>
              <span className="text-micro font-semibold uppercase tracking-[0.06em] text-tertiary shrink-0">
                {meta.label}
              </span>
            </div>
            <p className={`text-caption text-tertiary truncate ${isEmail ? "" : "font-mono"}`}>
              {dest.target}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2.5 shrink-0">
          {dest.failures > 0 && <StatusBadge label={`${dest.failures} failed`} tone="red" />}
          <StatusBadge
            label={
              !dest.verified
                ? "Awaiting verification"
                : dest.paused
                  ? "Auto-paused"
                  : active
                    ? "Active"
                    : "Paused"
            }
            tone={!dest.verified ? "amber" : dest.paused ? "red" : active ? "green" : "neutral"}
          />
        </div>
      </div>

      {!open && (
        <div className="flex items-center gap-6 mt-4 pt-4 border-t border-default">
          <div>
            <div className="text-micro text-tertiary">Sends</div>
            <div className="text-body font-medium text-primary mt-0.5">{dest.events}</div>
          </div>
          <span className="w-px h-8 bg-default" />
          <div>
            <div className="text-micro text-tertiary">Last delivery</div>
            <div className="text-body font-medium text-primary mt-0.5">{dest.lastDelivery}</div>
          </div>
        </div>
      )}

      {open && (
        <div className="flex flex-col gap-4 mt-4 pt-4 border-t border-default">
          {isEmail ? (
            <>
              <Field label="Verified recipient or distribution list">
                <input className={textCls} value={dest.target} readOnly />
              </Field>
              <div className="rounded-md border border-default bg-surface-light px-3.5 py-3 flex items-start gap-3">
                <span className="w-8 h-8 rounded-full bg-surface-white border border-default flex items-center justify-center shrink-0">
                  <i className="fa-solid fa-envelope-circle-check text-brand-dark-teal text-[13px]" />
                </span>
                <div>
                  <div className="text-body font-medium text-primary">Email configured</div>
                  <p className="text-caption text-tertiary mt-0.5">
                    {dest.verified
                      ? "Qualified leads will be emailed to this verified destination."
                      : "Delivery will begin once this recipient confirms the verification email."}
                  </p>
                </div>
              </div>
              <Field label="Delivery policy">
                <input
                  className={textCls}
                  value={emailPolicyLabel(dest.deliveryPolicy as EmailDeliveryPolicy | undefined)}
                  readOnly
                />
              </Field>
              <p className="text-caption text-tertiary">
                Verification links are valid for 24 hours. Opening an expired link automatically
                sends a replacement to the same address. Recipient changes create a new verification
                cycle and are made by replacing this destination.
              </p>
            </>
          ) : (
            <>
              <Field label="Destination URL" hint="HTTPS only">
                <input className={inputCls} value={dest.target} readOnly />
              </Field>
              <Field label="Signing secret" hint="Sent as X-FMC-Signature">
                <input
                  className={inputCls}
                  type="password"
                  value=""
                  placeholder="Shown once when created"
                  readOnly
                />
              </Field>
              <Field label="Delivery policy">
                <input
                  className={textCls}
                  value={webhookPolicyLabel(
                    dest.deliveryPolicy as WebhookDeliveryPolicy | undefined,
                  )}
                  readOnly
                />
              </Field>
              <p className="text-caption text-tertiary">
                HTTPS POST with no redirect following. URL, policy or secret changes require
                replacement and reverification.
              </p>
            </>
          )}

          <div className="rounded-md border border-default bg-surface-light px-3.5 py-3 text-caption text-secondary">
            Trigger:{" "}
            {isDailyEmail
              ? "daily at 08:00 Europe/London, containing every lead received since the previous email."
              : isDailyWebhook
                ? "daily at 08:00 Europe/London."
                : "every newly accepted or updated qualified lead."}
          </div>

          {!isEmail && (
            <div className="rounded-md border border-default px-3.5 py-3">
              <div className="flex items-center justify-between gap-3 mb-2.5">
                <span className="text-caption font-medium text-primary">Payload</span>
                <span className="inline-flex items-center gap-1.5 text-micro text-brand-dark-teal">
                  <i className="fa-solid fa-shield-halved text-[10px]" />
                  Fictitious example data
                </span>
              </div>
              <PayloadPreview compact daily={isDailyWebhook} />
            </div>
          )}

          <div className="rounded-md border border-default bg-surface-light px-3.5 py-2.5">
            <Toggle
              on={active}
              onToggle={() => setActive((v) => !v)}
              label={active ? "Destination active" : "Destination paused"}
            />
          </div>

          <button
            onClick={async () => {
              try {
                setError("");
                if (isEmail && !dest.verified) {
                  await api(
                    `/api/destinations/${encodeURIComponent(dest.id)}/resend-verification`,
                    { method: "POST" },
                  );
                  setVerificationResent(true);
                  setTimeout(() => setVerificationResent(false), 2500);
                  return;
                }
                await api(`/api/destinations/${encodeURIComponent(dest.id)}/test`, {
                  method: "POST",
                });
                setTested(true);
                setTimeout(() => setTested(false), 2500);
              } catch (problem) {
                setError(
                  problem instanceof Error ? problem.message : "Request could not be queued",
                );
              }
            }}
            disabled={!canEdit}
            className={`h-9 w-full rounded-xs border text-body font-medium flex items-center justify-center gap-2 transition-colors ${
              tested || verificationResent
                ? "border-status-green/40 bg-[rgba(86,205,174,0.12)] text-status-green"
                : "border-default text-button hover:bg-surface-light"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            <i
              className={`fa-solid ${
                tested || verificationResent
                  ? "fa-circle-check"
                  : isEmail
                    ? "fa-envelope"
                    : "fa-plug"
              } text-[13px]`}
            />
            {verificationResent
              ? "Verification email queued"
              : tested
                ? isEmail
                  ? "Synthetic test email queued"
                  : "Synthetic verification queued"
                : isEmail && !dest.verified
                  ? "Resend verification email"
                  : isEmail
                    ? "Send test email"
                    : "Send test payload"}
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                await api(
                  `/api/destinations/${encodeURIComponent(dest.id)}/${active ? "resume" : "pause"}`,
                  { method: "POST" },
                );
                await onRefresh();
                setOpen(false);
              }}
              disabled={!canEdit}
              className="flex-1 h-9 rounded-xs bg-brand-red text-on-brand text-body font-medium hover:bg-brand-red-hover transition-colors disabled:opacity-40"
            >
              {active ? "Resume destination" : "Pause destination"}
            </button>
            <button
              onClick={async () => {
                const password = window.prompt(
                  "Confirm your password to remove this destination and cancel queued deliveries",
                );
                if (!password) return;
                try {
                  setError("");
                  const reauth = await api<{ token: string }>("/api/reauthenticate", {
                    method: "POST",
                    body: JSON.stringify({ password, action: "destination.change" }),
                  });
                  await api(`/api/destinations/${encodeURIComponent(dest.id)}`, {
                    method: "DELETE",
                    body: JSON.stringify({ reauthenticationToken: reauth.token }),
                  });
                  await onRefresh();
                } catch (problem) {
                  setError(
                    problem instanceof Error ? problem.message : "Destination removal failed",
                  );
                }
              }}
              disabled={!canEdit}
              className="h-9 px-3 rounded-xs border border-status-red/40 text-status-red text-body font-medium disabled:opacity-40"
            >
              Remove
            </button>
          </div>
          {error && <p className="text-caption text-status-red">{error}</p>}
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        className="mt-4 h-9 w-full rounded-xs border border-default text-button text-body font-medium flex items-center justify-center gap-2 hover:bg-surface-light transition-colors"
      >
        <i className={`fa-solid ${open ? "fa-chevron-up" : "fa-sliders"} text-[13px]`} />
        {open ? "Collapse" : "Configure destination"}
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Destinations tab                                                    */
/* ------------------------------------------------------------------ */

function NewDestination({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: () => Promise<void>;
}) {
  const [type, setType] = useState<DestinationType>("email");
  const [label, setLabel] = useState("");
  const [target, setTarget] = useState("");
  const [emailDeliveryPolicy, setEmailDeliveryPolicy] = useState<EmailDeliveryPolicy>("immediate");
  const [deliveryPolicy, setDeliveryPolicy] = useState<WebhookDeliveryPolicy>("every_exponential");
  const [policyMenuOpen, setPolicyMenuOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [signingSecret, setSigningSecret] = useState("");
  const visibleDeliveryPolicies: Array<{
    id: EmailDeliveryPolicy | WebhookDeliveryPolicy;
    label: string;
    description: string;
  }> = type === "email" ? emailDeliveryPolicies : webhookDeliveryPolicies;

  const create = async () => {
    setSaving(true);
    setError("");
    try {
      const body =
        type === "email"
          ? {
              kind: "sales_email",
              label,
              recipients: [target.trim()],
              deliveryPolicy: emailDeliveryPolicy,
              authorisedRecipientConfirmation: confirmed,
            }
          : { kind: "webhook", label, url: target.trim(), deliveryPolicy };
      const created = await api<{ signingSecret?: string }>("/api/destinations", {
        method: "POST",
        body: JSON.stringify(body),
      });
      await onCreated();
      if (type === "webhook" && created.signingSecret) setSigningSecret(created.signingSecret);
      else onCancel();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "Destination could not be created");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageSection title="New destination">
      <div className="flex flex-col gap-4 max-w-[720px]">
        <div>
          <span className="text-caption font-medium text-primary">Delivery method</span>
          <div className="grid grid-cols-2 gap-3 mt-2">
            {(["webhook", "email"] as const).map((t) => {
              const m = typeMeta[t];
              const on = type === t;
              return (
                <button
                  key={t}
                  onClick={() => {
                    setType(t);
                    setPolicyMenuOpen(false);
                  }}
                  className={`flex items-start gap-3 rounded-md border px-4 py-3.5 text-left transition-colors ${
                    on
                      ? "border-brand-red bg-brand-red-tint"
                      : "border-default hover:bg-surface-light"
                  }`}
                >
                  <i
                    className={`fa-solid ${m.icon} text-[16px] mt-0.5 ${
                      on ? "text-brand-red" : "text-tertiary"
                    }`}
                  />
                  <span>
                    <span className="block text-body font-semibold text-primary">{m.label}</span>
                    <span className="block text-caption text-tertiary mt-0.5">{m.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Destination name">
            <input
              className={textCls}
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="e.g. Sales team alert"
            />
          </Field>
          <Field
            label={type === "email" ? "Recipients" : "Destination URL"}
            hint={type === "email" ? "One address per destination" : "HTTPS only"}
          >
            <input
              className={type === "email" ? textCls : inputCls}
              value={target}
              onChange={(event) => setTarget(event.target.value)}
              placeholder={type === "email" ? "sales@example.test" : "https://"}
            />
          </Field>
          <Field label="Delivery policy">
            <div className="relative">
              <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={policyMenuOpen}
                onClick={() => setPolicyMenuOpen((open) => !open)}
                className="w-full min-h-9 px-3 py-2 rounded-xs border border-default bg-surface-white text-left text-body text-primary focus:outline-none focus:border-brand-red/50 transition-colors flex items-center justify-between gap-3"
              >
                <span>
                  {type === "email"
                    ? emailPolicyLabel(emailDeliveryPolicy)
                    : webhookPolicyLabel(deliveryPolicy)}
                </span>
                <i
                  className={`fa-solid fa-chevron-down text-[11px] text-tertiary transition-transform ${policyMenuOpen ? "rotate-180" : ""}`}
                />
              </button>
              {policyMenuOpen && (
                <div
                  role="listbox"
                  className="absolute z-30 left-0 right-0 top-[calc(100%+6px)] overflow-hidden rounded-md border border-default bg-surface-white shadow-[var(--shadow-md)]"
                >
                  {visibleDeliveryPolicies.map((option) => {
                    const selected =
                      option.id === (type === "email" ? emailDeliveryPolicy : deliveryPolicy);
                    return (
                      <button
                        key={option.id}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => {
                          if (type === "email") {
                            setEmailDeliveryPolicy(option.id as EmailDeliveryPolicy);
                          } else {
                            setDeliveryPolicy(option.id as WebhookDeliveryPolicy);
                          }
                          setPolicyMenuOpen(false);
                        }}
                        className={`w-full px-3.5 py-3 text-left border-b border-default last:border-b-0 transition-colors flex items-start gap-3 ${
                          selected ? "bg-brand-red-tint" : "hover:bg-surface-light"
                        }`}
                      >
                        <span
                          className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                            selected ? "border-brand-red bg-brand-red" : "border-default"
                          }`}
                        >
                          {selected && <i className="fa-solid fa-check text-[8px] text-white" />}
                        </span>
                        <span>
                          <span className="block text-caption font-medium text-primary">
                            {option.label}
                          </span>
                          <span className="block text-micro text-tertiary mt-0.5">
                            {option.description}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </Field>
        </div>

        {type === "email" && (
          <label className="flex items-start gap-2.5 rounded-md border border-default bg-surface-info/40 px-4 py-3 text-caption text-secondary">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
              className="mt-0.5"
            />
            I confirm this fixed recipient or distribution list is authorised to process this
            client's lead PII. The verification message must be actioned within 24 hours before
            delivery starts. Opening an expired link automatically sends a replacement.
          </label>
        )}

        {type === "webhook" && (
          <div className="flex flex-col gap-2">
            <div>
              <div className="text-caption font-medium text-primary">Example webhook package</div>
              <p className="text-micro text-tertiary mt-0.5">
                This is the signed request shape your endpoint will receive for a lead update.
              </p>
            </div>
            <PayloadPreview daily={deliveryPolicy.startsWith("daily")} />
          </div>
        )}

        {error && <p className="text-caption text-status-red">{error}</p>}

        {signingSecret && (
          <div className="rounded-md border border-status-amber/40 bg-surface-light px-4 py-3">
            <div className="text-caption font-semibold text-primary">
              Copy the signing secret now
            </div>
            <div className="font-mono text-caption text-secondary break-all mt-1.5 select-all">
              {signingSecret}
            </div>
            <p className="text-micro text-tertiary mt-1.5">
              It is encrypted at rest and cannot be displayed again. Send a synthetic test payload
              to verify and activate this webhook.
            </p>
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            onClick={create}
            disabled={
              Boolean(signingSecret) ||
              saving ||
              !label ||
              !target ||
              (type === "email" && !confirmed)
            }
            className="h-10 px-5 rounded-xs bg-brand-red text-on-brand text-body font-semibold hover:bg-brand-red-hover transition-colors disabled:opacity-40"
          >
            {saving ? "Creating…" : "Create destination"}
          </button>
          <button
            onClick={onCancel}
            className="h-10 px-5 rounded-xs border border-default text-secondary text-body font-medium hover:bg-surface-white transition-colors"
          >
            {signingSecret ? "Done" : "Cancel"}
          </button>
        </div>
      </div>
    </PageSection>
  );
}

function DestinationsTab({ canEdit }: { canEdit: boolean }) {
  const [adding, setAdding] = useState(false);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [error, setError] = useState("");
  const refresh = async () => {
    try {
      const response = await api<{
        items: Array<{
          id: string;
          kind: "sales_email" | "webhook";
          label: string;
          active: boolean;
          pausedReason: string | null;
          consecutiveFailures: number;
          verifiedAt: string | null;
          recipients?: string[];
          url?: string;
          deliveryPolicy?: EmailDeliveryPolicy | WebhookDeliveryPolicy;
        }>;
      }>("/api/destinations");
      setDestinations(
        response.items.map((item) => ({
          id: item.id,
          name: item.label,
          type: item.kind === "sales_email" ? "email" : "webhook",
          target: item.recipients?.join(", ") ?? item.url ?? "",
          events:
            item.deliveryPolicy === "daily" || item.deliveryPolicy?.startsWith("daily")
              ? "Daily batch"
              : "All leads",
          active: item.active,
          verified: Boolean(item.verifiedAt),
          paused: Boolean(item.pausedReason),
          lastDelivery: "See activity log",
          failures: item.consecutiveFailures,
          queued: 0,
          ...(item.deliveryPolicy ? { deliveryPolicy: item.deliveryPolicy } : {}),
        })),
      );
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "Destinations could not be loaded");
    }
  };
  useEffect(() => {
    void refresh();
  }, []);
  const paused = destinations.filter((d) => d.paused);

  return (
    <div className="flex flex-col gap-[24px]">
      {paused.map((d) => (
        <div
          key={d.id}
          className="flex items-start gap-3 rounded-xl border border-status-red/40 bg-[rgba(255,101,80,0.08)] px-5 py-4"
        >
          <i className="fa-solid fa-triangle-exclamation text-[16px] text-status-red mt-0.5" />
          <div className="flex-1">
            <p className="text-body font-semibold text-primary">
              {d.name} was auto-paused after {d.failures} consecutive failures
            </p>
            <p className="text-caption text-secondary mt-0.5">
              {d.queued} leads are held in the queue and nothing has been lost. Fix the endpoint,
              then ask FMC support to resume delivery — they can replay the held leads for you.
            </p>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <StatusBadge label="FMC notified" tone="neutral" />
            <span className="text-micro text-tertiary">Held leads are kept until resumed</span>
          </div>
        </div>
      ))}

      <div className="flex items-start gap-3 rounded-xl border border-default bg-surface-info/40 px-5 py-4">
        <i className="fa-solid fa-paper-plane text-[16px] text-brand-dark-teal mt-0.5" />
        <div className="flex-1">
          <p className="text-body text-primary">
            When a lead arrives, Leads Manager sends it to every active destination — by webhook or
            by email.
          </p>
          <p className="text-caption text-secondary mt-0.5">
            Each verified destination receives every accepted lead. Previews and test sends never
            contain real personal data.
          </p>
        </div>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="h-9 px-3.5 rounded-xs bg-brand-red text-on-brand text-body font-medium flex items-center gap-2 hover:bg-brand-red-hover transition-colors shrink-0"
          >
            <i className="fa-solid fa-plus text-[12px]" />
            Add destination
          </button>
        )}
      </div>

      {adding && <NewDestination onCancel={() => setAdding(false)} onCreated={refresh} />}
      {error && <p className="text-caption text-status-red">{error}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        {destinations.map((d) => (
          <DestinationCard key={d.id} dest={d} canEdit={canEdit} onRefresh={refresh} />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Activity                                                            */
/* ------------------------------------------------------------------ */

interface Activity {
  id: string;
  direction: "out";
  channel: "webhook" | "sales_email";
  leadId: string | null;
  destination: string;
  status: "queued" | "sent" | "delivered" | "retry" | "failed" | "bounced" | "paused";
  responseCode: number | null;
  occurredAt: string;
}

const activityColumns: Column[] = [
  { label: "", align: "center", width: "52px" },
  { label: "Lead", align: "left" },
  { label: "Destination", align: "left", width: "190px" },
  { label: "Detail", align: "left", width: "200px" },
  { label: "When", align: "left", width: "120px" },
  { label: "Result", align: "left", width: "170px" },
];

function ActivityTab({ brand }: { brand: string }) {
  const [activity, setActivity] = useState<Activity[]>([]);
  const [error, setError] = useState("");
  useEffect(() => {
    api<{ items: Activity[] }>("/api/delivery/activity")
      .then((response) => setActivity(response.items))
      .catch((problem) =>
        setError(problem instanceof Error ? problem.message : "Activity unavailable"),
      );
  }, []);
  const failed = activity.filter((item) => ["failed", "bounced"].includes(item.status));

  const rows: React.ReactNode[][] = [];
  activity.forEach((a) => {
    const success = ["sent", "delivered"].includes(a.status);
    const failure = ["failed", "bounced"].includes(a.status);
    const tone = success ? "green" : failure ? "red" : "amber";

    rows.push([
      <span
        title={`Outbound · ${a.channel}`}
        className="inline-flex w-7 h-7 rounded-full items-center justify-center bg-surface-light text-secondary"
      >
        <i
          className={`fa-solid ${a.channel === "sales_email" ? "fa-envelope" : "fa-arrow-up"} text-[11px]`}
        />
      </span>,
      <span className="font-mono text-caption text-primary">
        {a.leadId ? a.leadId.slice(0, 13) : "Synthetic test"}
      </span>,
      <span className="text-body text-secondary">{a.destination}</span>,
      <span className="font-mono text-caption text-tertiary">
        {a.responseCode ? `HTTP ${a.responseCode}` : "No body retained"}
      </span>,
      <span className="text-body text-secondary">
        {new Date(a.occurredAt).toLocaleString("en-GB")}
      </span>,
      <StatusBadge label={a.status} tone={tone as "green" | "red" | "amber"} />,
    ]);
  });

  return (
    <div className="flex flex-col gap-[24px]">
      <HubspotIntakeSummary brand={brand} />
      <PageSection
        title="Delivery activity"
        right={
          failed.length > 0 ? (
            <span className="inline-flex items-center gap-2 text-caption text-tertiary">
              <i className="fa-solid fa-circle-info text-[12px]" />
              {failed.length} failed · retries and replays are run by FMC support
            </span>
          ) : (
            <span className="text-caption text-tertiary">All traffic healthy</span>
          )
        }
        bodyClassName="px-[20px] pt-[16px] pb-[20px]"
      >
        {error ? (
          <p className="py-8 text-center text-caption text-status-red">{error}</p>
        ) : rows.length ? (
          <>
            <ReportTable columns={activityColumns} rows={rows} />
            <Pagination from={1} to={activity.length} total={activity.length} />
          </>
        ) : (
          <p className="py-8 text-center text-body text-tertiary">No delivery attempts yet.</p>
        )}
      </PageSection>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Screen                                                              */
/* ------------------------------------------------------------------ */

type Tab = "destinations" | "activity";

export function Delivery() {
  const seed = useScreenInit();
  const session = useSession();
  const [tab, setTab] = useState<Tab>(seed.tab === "activity" ? "activity" : "destinations");

  const tabs = [
    { key: "destinations" as const, label: "Destinations", icon: "fa-paper-plane" },
    { key: "activity" as const, label: "Activity", icon: "fa-list-ul" },
  ];

  return (
    <div className="flex flex-col gap-[24px]">
      <div className="flex items-center gap-1 border-b border-default -mt-1">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-3 text-body font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? "border-brand-red text-brand-red"
                : "border-transparent text-secondary hover:text-primary"
            }`}
          >
            <i className={`fa-solid ${t.icon} text-[13px]`} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "destinations" && <DestinationsTab canEdit={session.canEditConfig} />}
      {tab === "activity" && <ActivityTab brand={session.brand} />}
    </div>
  );
}
